import React, { useEffect } from 'react';
import { Modal, Form, Input } from 'antd';
import { t } from '../../../../i18n';
import { parseDecimalStringToCents, sanitizeDecimalInput } from '../utils/perVehicleCost';

/** 取消原因最大长度（与后端校验一致） */
const CANCEL_REASON_MAX_LENGTH = 200;

interface CancelTaskFormValues {
    cancelReason: string;
    cancelCost: string;
}

export interface CancelTaskModalProps {
    open: boolean;
    /** 弹窗中展示的任务单号 */
    taskNumber?: string;
    confirmLoading?: boolean;
    /** 确认取消：取消原因（已去首尾空格）、取消成本（美分） */
    onOk: (cancelReason: string, cancelCostCents: number) => void | Promise<void>;
    onCancel: () => void;
}

/**
 * PC 取消任务弹窗：填写取消原因与取消成本（美元，提交时转为美分；取消成本即单车成本）
 */
const CancelTaskModal: React.FC<CancelTaskModalProps> = ({ open, taskNumber, confirmLoading = false, onOk, onCancel }) => {
    const [form] = Form.useForm<CancelTaskFormValues>();

    // 每次打开都清空上一次的输入
    useEffect(() => {
        if (open) {
            form.resetFields();
        }
    }, [open, form]);

    const handleOk = async () => {
        const values = await form.validateFields();
        await onOk(values.cancelReason.trim(), parseDecimalStringToCents(values.cancelCost));
    };

    return (
        <Modal
            open={open}
            title={t('tms.task.cancelModal.title')}
            okText={t('tms.task.buttons.confirm')}
            cancelText={t('tms.task.buttons.cancel')}
            confirmLoading={confirmLoading}
            onOk={handleOk}
            onCancel={onCancel}
            destroyOnClose
            maskClosable={false}
        >
            <div className="mb-4">
                <span className="text-gray-500">{t('tms.task.cancelModal.taskNumber')}：</span>
                <span>{taskNumber || '-'}</span>
            </div>
            <Form form={form} layout="vertical" preserve={false}>
                <Form.Item
                    name="cancelReason"
                    label={t('tms.task.cancelModal.cancelReason')}
                    rules={[
                        { required: true, whitespace: true, message: t('tms.task.cancelModal.cancelReasonRequired') },
                    ]}
                >
                    <Input.TextArea
                        rows={3}
                        maxLength={CANCEL_REASON_MAX_LENGTH}
                        showCount
                        placeholder={t('tms.task.cancelModal.cancelReasonPlaceholder')}
                    />
                </Form.Item>
                <Form.Item
                    name="cancelCost"
                    label={t('tms.task.cancelModal.cancelCost')}
                    getValueFromEvent={(event: React.ChangeEvent<HTMLInputElement>) => sanitizeDecimalInput(event.target.value)}
                    rules={[
                        { required: true, whitespace: true, message: t('tms.task.cancelModal.cancelCostRequired') },
                    ]}
                >
                    <Input placeholder={t('tms.task.cancelModal.cancelCostPlaceholder')} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CancelTaskModal;
